/**
 * Research-Backed Cycle Facts Database
 * Scientific findings and statistics about menstrual cycle phases
 * to help partners understand hormonal changes and their effects
 */

export const CYCLE_FACTS = {
  Menstrual: [
    {
      id: 'mens-1',
      fact: "Prostaglandins (hormone-like compounds) trigger uterine contractions during menstruation. The same compounds can affect the digestive system, which is why up to 73% of women experience GI symptoms like cramping or diarrhea during their period.",
      practical: "Keep digestive-friendly foods available and be understanding if she needs more bathroom breaks.",
      source: "BMC Women's Health, 2014"
    },
    {
      id: 'mens-2',
      fact: "Iron levels drop significantly during menstruation. Women lose an average of 30-40ml of blood per cycle, and heavy periods can lead to iron deficiency, causing fatigue and difficulty concentrating.",
      practical: "Iron-rich foods like red meat, spinach, or dark chocolate can genuinely help her energy levels right now.",
      source: "American Journal of Clinical Nutrition"
    },
    {
      id: 'mens-3',
      fact: "Pain sensitivity actually increases during menstruation due to dropping estrogen levels. Studies show women's pain thresholds are lowest during the first few days of their period.",
      practical: "That headache or backache she mentions isn't exaggerated - her body literally feels pain more intensely right now.",
      source: "Journal of Pain Research, 2018"
    },
    {
      id: 'mens-4',
      fact: "Body temperature drops to its lowest point during menstruation as progesterone levels fall. This can make her feel colder than usual.",
      practical: "Don't be surprised if she wants extra blankets or turns up the heat - her internal thermostat has shifted.",
      source: "Fertility and Sterility Journal"
    },
    {
      id: 'mens-5',
      fact: "The brain's prefrontal cortex shows reduced activity during menstruation, which can affect decision-making and focus. This isn't weakness - it's biology.",
      practical: "This might not be the best time to make major decisions together. Save big discussions for next week.",
      source: "Neuroscience & Biobehavioral Reviews, 2020"
    },
    {
      id: 'mens-6',
      fact: "Melatonin production increases during menstruation, which explains why 70% of women report feeling more tired and needing more sleep during their period.",
      practical: "Let her sleep in if possible - her body is literally producing more sleep hormones.",
      source: "Sleep Medicine Reviews"
    },
    {
      id: 'mens-7',
      fact: "Magnesium levels drop during menstruation, contributing to cramps, mood changes, and chocolate cravings. Chocolate contains magnesium, so those cravings are actually her body asking for what it needs.",
      practical: "Those chocolate cravings aren't just in her head - her body is seeking magnesium. Quality dark chocolate actually helps.",
      source: "Journal of Women's Health"
    },
    {
      id: 'mens-8',
      fact: "Studies show that menstrual cramps can be as painful as a heart attack, according to research. The intensity is caused by the uterus contracting to shed its lining.",
      practical: "When she says the pain is severe, believe her. Heat pads, pain relief, and rest genuinely help.",
      source: "American Academy of Family Physicians"
    }
  ],
  
  Follicular: [
    {
      id: 'foll-1',
      fact: "Estrogen rises rapidly during the follicular phase, increasing dopamine production. This creates a natural 'high' - she'll likely feel more optimistic, creative, and socially energized.",
      practical: "This is a great time for date nights, trying new activities together, or tackling projects she's been putting off.",
      source: "Frontiers in Neuroscience, 2019"
    },
    {
      id: 'foll-2',
      fact: "Verbal fluency peaks during the follicular phase. Research shows women perform 10-15% better on verbal tasks when estrogen is high.",
      practical: "She may want to talk more and communicate better now. Great time for deep conversations or working through issues.",
      source: "Psychoneuroendocrinology Journal"
    },
    {
      id: 'foll-3',
      fact: "The follicular phase is when the immune system is strongest. Estrogen has anti-inflammatory effects and boosts immune cell production.",
      practical: "If she's been fighting off a cold, she's more likely to bounce back quickly during this phase.",
      source: "Journal of Immunology"
    },
    {
      id: 'foll-4',
      fact: "Testosterone also rises during this phase, peaking just before ovulation. This increases her sex drive, competitiveness, and confidence.",
      practical: "She may initiate more, take on challenges, or feel more assertive. Support her ambitions right now.",
      source: "Hormones and Behavior Journal"
    },
    {
      id: 'foll-5',
      fact: "Skin appearance peaks during the follicular phase. Estrogen increases collagen production and skin hydration, which is why many women notice clearer, glowing skin.",
      practical: "Compliments about her appearance will land especially well - she's likely feeling more confident too.",
      source: "Journal of Clinical and Aesthetic Dermatology"
    },
    {
      id: 'foll-6',
      fact: "Energy metabolism increases by up to 10% during the follicular phase. Her body burns carbohydrates more efficiently, making this ideal for high-intensity activities.",
      practical: "If she wants to hit the gym, go hiking, or try something active, she'll have more stamina now.",
      source: "British Journal of Sports Medicine"
    },
    {
      id: 'foll-7',
      fact: "Memory consolidation improves during high-estrogen phases. Studies show women remember emotional events more vividly and learn new information faster.",
      practical: "Great time to learn something new together - she'll retain information better during this phase.",
      source: "Current Directions in Psychological Science"
    },
    {
      id: 'foll-8',
      fact: "Risk tolerance increases during the follicular phase. Research shows women are more likely to try new things, take chances, and step outside their comfort zone.",
      practical: "Suggest that adventurous date or new restaurant - she's more likely to say yes right now.",
      source: "Psychological Science Journal"
    }
  ],
  
  Ovulation: [
    {
      id: 'ovul-1',
      fact: "During ovulation, estrogen peaks at 200-400 pg/ml (compared to 30-100 in other phases). This hormonal surge affects everything from her voice pitch to facial attractiveness.",
      practical: "Studies show both men and women rate women as more attractive during ovulation. She's literally glowing.",
      source: "Evolution and Human Behavior, 2018"
    },
    {
      id: 'ovul-2',
      fact: "Voice pitch actually rises during ovulation - by about 1-2 Hz on average. This subtle change is subconsciously perceived as more attractive and feminine.",
      practical: "You might notice her voice sounds slightly different - this is a real biological change.",
      source: "Physiology & Behavior Journal"
    },
    {
      id: 'ovul-3',
      fact: "Cervical mucus changes to an egg-white consistency during ovulation, becoming more alkaline to protect and nourish sperm. The body is biologically preparing for potential conception.",
      practical: "This is peak fertility time - be aware if pregnancy prevention is important to you both.",
      source: "Fertility and Sterility Journal"
    },
    {
      id: 'ovul-4',
      fact: "The brain's reward system is most active during ovulation. Dopamine and oxytocin surge, making social interactions, bonding, and intimacy feel more rewarding.",
      practical: "Quality time together hits different right now - she'll feel more connected from shared experiences.",
      source: "Hormones and Behavior, 2019"
    },
    {
      id: 'ovul-5',
      fact: "Spatial awareness and coordination peak during ovulation. Studies show women perform better on tasks requiring spatial skills during this phase.",
      practical: "She might surprise you with her gaming skills or parking abilities right now - coordination is heightened.",
      source: "Neuropsychologia Journal"
    },
    {
      id: 'ovul-6',
      fact: "Some women (20-40%) experience 'mittelschmerz' - ovulation pain. This is caused by the follicle rupturing to release the egg and can range from a twinge to sharp pain.",
      practical: "If she mentions one-sided lower abdominal pain, it's likely ovulation-related and usually passes within 24 hours.",
      source: "American College of Obstetricians and Gynecologists"
    },
    {
      id: 'ovul-7',
      fact: "Attraction patterns shift during ovulation. Research shows women subconsciously prefer more masculine traits during fertile days - including deeper voices, dominant behavior, and physical symmetry.",
      practical: "Your confidence and assertiveness may be especially attractive to her right now.",
      source: "Psychological Science, 2014"
    },
    {
      id: 'ovul-8',
      fact: "The sense of smell becomes significantly more acute during ovulation. Women can detect certain scents (especially male pheromones) up to 10,000 times better than at other times.",
      practical: "Shower before date night - she'll notice your scent much more intensely during this phase.",
      source: "Chemical Senses Journal"
    }
  ],
  
  "Early Luteal": [
    {
      id: 'elut-1',
      fact: "Progesterone rises significantly after ovulation, increasing from near-zero to 10-20 ng/ml. This hormone has sedative-like effects on the brain, similar to a mild tranquilizer.",
      practical: "She's not being lazy - progesterone literally has calming, sleepy effects. Cozy nights in are ideal.",
      source: "Frontiers in Cellular Neuroscience"
    },
    {
      id: 'elut-2',
      fact: "Body temperature rises 0.3-0.5°C (0.5-1°F) after ovulation due to progesterone. This slight increase lasts until menstruation begins.",
      practical: "She might feel warmer than usual and prefer cooler environments. Sleep temperature matters more now.",
      source: "Fertility and Sterility Journal"
    },
    {
      id: 'elut-3',
      fact: "Progesterone slows down digestion by relaxing smooth muscle tissue. This can cause bloating, constipation, and feeling full faster.",
      practical: "Smaller, more frequent meals may feel better than large ones. Bloating is hormonal, not overeating.",
      source: "Gastroenterology Journal"
    },
    {
      id: 'elut-4',
      fact: "The immune system shifts during the luteal phase, becoming slightly suppressed. This is the body preparing for potential pregnancy (to not reject a fertilized egg).",
      practical: "She may be more susceptible to catching colds or infections right now. Extra vitamin C doesn't hurt.",
      source: "Brain, Behavior, and Immunity Journal"
    },
    {
      id: 'elut-5',
      fact: "Serotonin production decreases as estrogen drops, which can affect mood regulation. This is why mood becomes less stable compared to the first half of the cycle.",
      practical: "Patience is key. Her mood fluctuations are linked to real neurochemical changes, not personality.",
      source: "Journal of Clinical Psychiatry"
    },
    {
      id: 'elut-6',
      fact: "The nesting instinct peaks during the luteal phase. Women often feel compelled to organize, clean, and prepare their environment.",
      practical: "If she suddenly wants to reorganize the closet or deep-clean, this is a biological urge. Help or stay out of the way.",
      source: "Evolution and Human Behavior"
    },
    {
      id: 'elut-7',
      fact: "Appetite increases by 100-500 calories per day during the luteal phase. Basal metabolic rate actually increases, requiring more fuel.",
      practical: "Her increased appetite is her body's real need for more energy. Don't comment on how much she's eating.",
      source: "American Journal of Clinical Nutrition"
    },
    {
      id: 'elut-8',
      fact: "Carbohydrate cravings spike because the body is seeking serotonin precursors. Carbs help produce serotonin, which is why comfort foods are genuinely comforting.",
      practical: "That pasta or bread craving is her brain seeking feel-good chemicals. Whole grains are a good compromise.",
      source: "Nutrition Research Reviews"
    }
  ],
  
  "Late Luteal/PMS": [
    {
      id: 'llut-1',
      fact: "PMS affects 75-90% of women to some degree. The rapid drop in both estrogen and progesterone in the days before menstruation creates a hormonal 'withdrawal' effect.",
      practical: "This isn't in her head - her body is experiencing something similar to withdrawal from mood-stabilizing hormones.",
      source: "American College of Obstetricians and Gynecologists"
    },
    {
      id: 'llut-2',
      fact: "PMDD (Premenstrual Dysphoric Disorder) affects 3-8% of women and is characterized by severe mood symptoms. It's now recognized as a legitimate medical condition in the DSM-5.",
      practical: "If her symptoms seem extreme every month, PMDD might be worth discussing with a doctor. It's treatable.",
      source: "Journal of Women's Health, 2020"
    },
    {
      id: 'llut-3',
      fact: "Water retention increases due to hormonal changes, with women gaining an average of 1-3 lbs of water weight before their period. This is temporary and usually resolves within 2-3 days of menstruation.",
      practical: "Feeling bloated and 'fat' is water weight, not real weight gain. Loose, comfortable clothes help.",
      source: "International Journal of Women's Health"
    },
    {
      id: 'llut-4',
      fact: "The amygdala (brain's emotion center) shows increased reactivity during the late luteal phase. Emotional responses are genuinely amplified at a neural level.",
      practical: "Her emotional reactions are stronger because her brain is literally processing emotions differently right now.",
      source: "Social Cognitive and Affective Neuroscience"
    },
    {
      id: 'llut-5',
      fact: "Sleep quality declines for 30% of women during the premenstrual phase. Progesterone withdrawal affects REM sleep, and body temperature changes disrupt sleep cycles.",
      practical: "She may be sleep-deprived even if she's in bed for 8 hours. Be understanding about tiredness.",
      source: "Sleep Medicine Reviews"
    },
    {
      id: 'llut-6',
      fact: "Headaches and migraines increase by 25% during the premenstrual window. The drop in estrogen affects blood vessel dilation in the brain.",
      practical: "Menstrual migraines are a real phenomenon. Dark rooms, rest, and proper medication help.",
      source: "Headache: The Journal of Head and Face Pain"
    },
    {
      id: 'llut-7',
      fact: "Breast tenderness affects up to 70% of women premenstrually. Progesterone causes breast tissue to swell and retain fluid.",
      practical: "Avoid tight hugs or anything that puts pressure on her chest area. It's genuinely uncomfortable.",
      source: "Journal of Obstetrics and Gynaecology"
    },
    {
      id: 'llut-8',
      fact: "Anxiety increases during PMS because GABA (the brain's calming neurotransmitter) receptors become less sensitive to progesterone metabolites.",
      practical: "Increased anxiety before her period is neurochemical. Reassurance and calm environments genuinely help.",
      source: "Psychoneuroendocrinology"
    },
    {
      id: 'llut-9',
      fact: "Concentration and focus decline during PMS. Studies show a 10-15% decrease in cognitive performance on complex tasks during the late luteal phase.",
      practical: "If she seems scattered or forgetful, it's not carelessness. Her brain is working differently right now.",
      source: "Frontiers in Psychology"
    },
    {
      id: 'llut-10',
      fact: "The drop in serotonin during PMS increases cravings for sugary and fatty foods because they provide quick dopamine hits. It's the brain seeking pleasure to compensate for lower baseline mood.",
      practical: "Those junk food cravings are her brain seeking feel-good chemicals. Don't judge, maybe just join her.",
      source: "Appetite Journal"
    }
  ],

  // General facts that can appear any time
  General: [
    {
      id: 'gen-1',
      fact: "The average menstrual cycle is 28 days, but anywhere from 21-35 days is considered normal. Cycle length can vary by up to 7-9 days from month to month.",
      practical: "Don't be surprised if her period doesn't arrive exactly when expected. Variation is normal.",
      source: "Obstetrics & Gynecology"
    },
    {
      id: 'gen-2',
      fact: "Hormone fluctuations affect more than 150 different systems in a woman's body, including cardiovascular, respiratory, immune, and nervous systems.",
      practical: "The menstrual cycle affects her entire body, not just her reproductive system. It's whole-body wellness.",
      source: "Endocrine Reviews"
    },
    {
      id: 'gen-3',
      fact: "Women who live together often sync their cycles - this is called the McClintock effect. Research suggests pheromones may influence cycle timing.",
      practical: "If multiple women in her life seem to be on the same cycle, there may be science behind it.",
      source: "Nature Journal (contested but culturally relevant)"
    },
    {
      id: 'gen-4',
      fact: "Chronic stress can delay or stop ovulation entirely by suppressing GnRH (gonadotropin-releasing hormone). The body prioritizes survival over reproduction under stress.",
      practical: "If her cycle is irregular during stressful times, it's her body's protective response. Help reduce stress.",
      source: "Fertility and Sterility"
    },
    {
      id: 'gen-5',
      fact: "Exercise can reduce PMS symptoms by 30-50% by boosting endorphins and improving circulation. However, over-exercising can disrupt the cycle entirely.",
      practical: "Gentle exercise together can help her symptoms, but she knows her body best.",
      source: "Journal of Psychosomatic Obstetrics"
    }
  ]
};

/**
 * Get a random fact for a specific phase
 * @param {string} phase - Current cycle phase
 * @returns {Object} Random fact object
 */
export const getRandomFact = (phase) => {
  // Map phase names to our database keys
  const phaseMap = {
    'Menstrual': 'Menstrual',
    'Follicular': 'Follicular',
    'Ovulation': 'Ovulation',
    'Early Luteal': 'Early Luteal',
    'Late Luteal': 'Late Luteal/PMS',
    'Late Luteal/PMS': 'Late Luteal/PMS',
    'PMS': 'Late Luteal/PMS'
  };

  const mappedPhase = phaseMap[phase] || 'General';
  const facts = CYCLE_FACTS[mappedPhase] || CYCLE_FACTS.General;
  
  // Return a random fact from the phase
  const randomIndex = Math.floor(Math.random() * facts.length);
  return facts[randomIndex];
};

/**
 * Get all facts for a specific phase
 * @param {string} phase - Cycle phase
 * @returns {Array} Array of fact objects
 */
export const getFactsByPhase = (phase) => {
  const phaseMap = {
    'Menstrual': 'Menstrual',
    'Follicular': 'Follicular',
    'Ovulation': 'Ovulation',
    'Early Luteal': 'Early Luteal',
    'Late Luteal': 'Late Luteal/PMS',
    'Late Luteal/PMS': 'Late Luteal/PMS',
    'PMS': 'Late Luteal/PMS'
  };

  const mappedPhase = phaseMap[phase] || 'General';
  return CYCLE_FACTS[mappedPhase] || [];
};

/**
 * Get a fact that hasn't been shown recently (uses localStorage to track)
 * @param {string} phase - Current cycle phase
 * @returns {Object} Fact object
 */
export const getUnseenFact = (phase) => {
  const phaseMap = {
    'Menstrual': 'Menstrual',
    'Follicular': 'Follicular',
    'Ovulation': 'Ovulation',
    'Early Luteal': 'Early Luteal',
    'Late Luteal': 'Late Luteal/PMS',
    'Late Luteal/PMS': 'Late Luteal/PMS',
    'PMS': 'Late Luteal/PMS'
  };

  const mappedPhase = phaseMap[phase] || 'General';
  const facts = CYCLE_FACTS[mappedPhase] || CYCLE_FACTS.General;
  
  // Get seen facts from localStorage
  const seenKey = `cyclecoach_seen_facts_${mappedPhase}`;
  const seenFacts = JSON.parse(localStorage.getItem(seenKey) || '[]');
  
  // Find unseen facts
  const unseenFacts = facts.filter(f => !seenFacts.includes(f.id));
  
  // If all facts have been seen, reset and start over
  if (unseenFacts.length === 0) {
    localStorage.setItem(seenKey, '[]');
    return facts[Math.floor(Math.random() * facts.length)];
  }
  
  // Get random unseen fact and mark as seen
  const fact = unseenFacts[Math.floor(Math.random() * unseenFacts.length)];
  seenFacts.push(fact.id);
  localStorage.setItem(seenKey, JSON.stringify(seenFacts));
  
  return fact;
};
